import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PlatformResponseDTO } from '@libs/dtos/platform/platform.dto';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PlatformService } from '@services/platforms/platform.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { TableBase } from '@shared/bases/table.base';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TableComponent } from '@shared/components/table/table.component';
import { TableAction } from '@shared/components/table/table.model';
import { SharedModule } from '@shared/modules/shared.module';
import { catchError, Observable, of } from 'rxjs';
import { PlatformCrudComponent } from './platform-crud/platform-crud.component';

@Component({
	selector: 'app-platforms',
	imports: [SharedModule, TableComponent, ButtonComponent, PlatformCrudComponent],
	templateUrl: './platform.component.html',
	styleUrl: './platform.component.scss',
})
export class PlatformComponent extends TableBase implements OnInit {
	@ViewChild('crudPlatform') crudPlatform!: PlatformCrudComponent;

	data: PlatformResponseDTO[] = [];

	// Column headers
	headers: { field: string; header: string }[] = [];

	// ====== Pagination Configuration ======
	override mainField = 'name';
	override sortField = this.mainField;
	override limit = 10;
	loadError = false;

	constructor(
		private platformService: PlatformService,
		private messageAlertService: MessageAlertService,
		private confirmDialogService: ConfirmDialogService,
		private cdr: ChangeDetectorRef,
		override viewportService: ViewportService,
		private translocoService: TranslocoService,
	) {
		super(viewportService);
		this.headers = [
			{ field: 'name', header: this.translocoService.translate('platformsCrud.headers.name') },
			{ field: 'link', header: this.translocoService.translate('platformsCrud.headers.url') },
		];
	}

	override loadData(): void {
		this.loadPlatforms();
	}

	ngOnInit(): void {
		this.limit = this.isMobile ? this.DEFAULT_LIMIT_MOBILE : this.DEFAULT_LIMIT;
		this.loadPlatforms();
	}

	/** After the form saves: an edit replaces its row; a new platform reloads (its page/position is the API's) */
	onPlatformSaved(event: { platform: PlatformResponseDTO; created: boolean }): void {
		if (event.created) {
			this.loadPlatforms();
			return;
		}
		this.data = this.data.map((p) => (p.id === event.platform.id ? event.platform : p));
		this.cdr.detectChanges();
	}

	/**
	 * Fetches platforms with pagination and sorting options.
	 */
	loadPlatforms(): void {
		this.isLoading = true;
		this.loadError = false;
		this.cdr.markForCheck();

		const params = {
			page: this.page,
			limit: this.limit,
			sort_by: this.sortField,
			sort_order: (this.sortOrder === 1 ? 'asc' : 'desc') as 'asc' | 'desc',
		};

		this.platformService
			.getPlatforms(params)
			.pipe(
				catchError((err) => {
					console.error('Error loading platforms:', err);
					this.data = [];
					this.totalRecords = 0;
					this.isLoading = false;
					this.loadError = true;
					this.cdr.detectChanges();
					return of(null);
				}),
			)
			.subscribe((response) => {
				if (response?.data) {
					this.data = response.data;
					this.totalRecords = response.total;
				}
				this.isLoading = false;
				this.cdr.detectChanges();
			});
	}

	/**
	 * Opens the platform form in update mode.
	 */
	onUpdatePlatform(platform: PlatformResponseDTO): void {
		if (!platform?.id) {
			console.error('No platform ID provided for update');
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('platformsCrud.messages.noValidId'),
			);
			return;
		}

		if (!this.crudPlatform) {
			console.error('CrudPlatform component not found');
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('platformsCrud.messages.editNotAvailable'),
			);
			return;
		}

		this.messageAlertService.showMessage(
			'info',
			this.translocoService.translate('platformsCrud.messages.editMode'),
			this.translocoService.translate('platformsCrud.messages.editing', { name: platform.name }),
		);
		this.crudPlatform.show(platform);
	}

	/**
	 * Handles the deletion of one or multiple platforms.
	 */
	onDeletePlatform(platforms: PlatformResponseDTO | PlatformResponseDTO[]): void {
		if (!platforms || (Array.isArray(platforms) && platforms.length === 0)) {
			console.error('No platforms provided for deletion');
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('platformsCrud.messages.noSelection'),
			);
			return;
		}

		const platformArray = Array.isArray(platforms) ? platforms : [platforms];
		const target =
			platformArray.length === 1
				? this.translocoService.translate('platformsCrud.messages.deleteTarget', {
						name: platformArray[0].name,
					})
				: this.translocoService.translate('platformsCrud.messages.deleteTargetMany', {
						count: platformArray.length,
					});

		this.confirmDialogService.confirmAndExecute(
			'delete',
			target,
			(): Observable<unknown> =>
				platformArray.length === 1
					? this.platformService.deletePlatform(platformArray[0].id.toString())
					: this.platformService.bulkDeletePlatforms(platformArray.map((item) => item.id)), // every selected row in one request
			{
				onSuccess: () => this.loadPlatforms(),
				errorMessage: this.translocoService.translate('platformsCrud.messages.deleteInUse'),
			},
		);
	}

	getMenuActions(platform: PlatformResponseDTO): TableAction[] {
		return [
			{
				id: 1,
				label: this.translocoService.translate('platformsCrud.actions.edit'),
				icon: 'Edit',
				action: 'update',
				command: () => this.onUpdatePlatform(platform),
			},
			{
				id: 2,
				label: this.translocoService.translate('platformsCrud.actions.delete'),
				icon: 'Trash',
				action: 'delete',
				class: 'red',
				separator: true,
				command: () => this.onDeletePlatform(platform),
			},
		];
	}

	menuActionTriggered(event: { action: string; data: PlatformResponseDTO | PlatformResponseDTO[] }): void {
		if (!event?.action || !event.data) return;

		switch (event.action) {
			case 'delete':
				this.onDeletePlatform(event.data);
				break;
			case 'update':
				this.onUpdatePlatform(event.data as PlatformResponseDTO);
				break;
			default:
				console.warn('Unknown action:', event.action);
		}
	}

	onRowSelected(platform: PlatformResponseDTO) {
		this.onUpdatePlatform(platform);
	}
}
